import { useState, useEffect, useRef } from "react";
import { LogOut, Plus, Pencil, Trash2, Package, CreditCard, FileText, X, ChevronLeft, Tag, Upload, Loader2, Settings, Truck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_LABELS = {
  shipping: "Shipping Information",
  returns: "Returns & Exchanges",
  contact: "Contact Us",
  faq: "FAQ",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  cookies: "Cookie Policy",
};

// ── Admin Login ────────────────────────────
function AdminLoginForm({ onLogin, t }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch { toast.error("Invalid password"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-login-form">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6">
        <h1 className="text-3xl text-center mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{t("admin.title")}</h1>
        <Input type="password" placeholder={t("admin.password")} value={password} onChange={(e) => setPassword(e.target.value)}
          className="border-b border-[#E6E2DD] border-t-0 border-l-0 border-r-0 rounded-none bg-transparent px-0 focus-visible:ring-0 text-sm tracking-wide" data-testid="admin-password-input" />
        <button type="submit" disabled={loading} className="btn-luxury w-full" data-testid="admin-login-btn">
          {loading ? t("common.loading") : t("admin.login")}
        </button>
      </form>
    </div>
  );
}

// ── Product Form ───────────────────────────
function ProductForm({ product, token, onSave, onCancel, t }) {
  const [form, setForm] = useState({
    name: product?.name || "", brand: product?.brand || "", category: product?.category || "",
    price: product?.price || "", description: product?.description || "",
    images: product?.images?.join("\n") || "", featured: product?.featured || false, in_stock: product?.in_stock ?? true,
    stock_quantity: product?.stock_quantity ?? 0,
    discount: product?.discount ?? 0,
    condition: product?.condition || "used",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 2000, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) { resolve(file); return; }
      // HEIC files: browser can't decode via canvas — upload raw, backend converts
      const isHeic = /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type);
      if (isHeic) { resolve(file); return; }
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Could not read file"));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error("Could not decode image"));
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > maxWidth) { h = Math.round((h * maxWidth) / w); w = maxWidth; }
          if (h > maxWidth) { w = Math.round((w * maxWidth) / h); h = maxWidth; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          canvas.toBlob((blob) => {
            if (!blob) { reject(new Error("Compression produced empty blob")); return; }
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
          }, "image/jpeg", quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    // NOTE: Do NOT set Content-Type manually — axios/browser will set multipart boundary
    const config = { headers: { Authorization: `Bearer ${token}` } };
    let ok = 0;
    const failures = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const prepared = await compressImage(file);
        const formData = new FormData();
        formData.append("file", prepared);
        const res = await axios.post(`${API}/admin/upload`, formData, config);
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        setForm((f) => ({
          ...f,
          images: f.images ? f.images + "\n" + imageUrl : imageUrl,
        }));
        ok++;
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "unknown error";
        failures.push({ name: file.name, reason: detail });
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }
    if (ok > 0 && failures.length === 0) {
      toast.success(ok > 1 ? `${ok} images uploaded` : "Image uploaded");
    } else if (ok > 0 && failures.length > 0) {
      toast.warning(`${ok} uploaded, ${failures.length} failed. First error: ${failures[0].name} — ${failures[0].reason}`);
    } else {
      toast.error(`Upload failed: ${failures[0]?.name || ""} — ${failures[0]?.reason || "unknown"}`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { in_stock, ...rest } = form;
      const data = {
        ...rest,
        price: parseFloat(form.price) || 0,
        images: form.images.split("\n").filter(Boolean),
        stock_quantity: parseInt(form.stock_quantity) || 0,
        discount: Math.min(100, Math.max(0, parseInt(form.discount) || 0)),
      };
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (product?.id) await axios.put(`${API}/admin/products/${product.id}`, data, config);
      else await axios.post(`${API}/admin/products`, data, config);
      toast.success(product?.id ? "Product updated" : "Product created");
      onSave();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        onSave();
        return;
      }
      toast.error(msg || t("common.error"));
    }
    finally { setSaving(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Extract image URLs for preview
  const imageList = form.images.split("\n").filter(Boolean);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-lg" data-testid="product-form">
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.name")}</label>
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="border-[#E6E2DD] bg-transparent" data-testid="product-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.brand")}</label>
          <Input value={form.brand} onChange={(e) => set("brand", e.target.value)} required className="border-[#E6E2DD] bg-transparent" data-testid="product-brand-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.category")}</label>
          <Input value={form.category} onChange={(e) => set("category", e.target.value)} required className="border-[#E6E2DD] bg-transparent" data-testid="product-category-input" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.price")}</label>
        <Input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} required className="border-[#E6E2DD] bg-transparent" data-testid="product-price-input" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.description")}</label>
        <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
          className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-none focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" data-testid="product-description-input" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.images")}</label>
        <div className="flex gap-2 mb-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif,image/heic,image/heif"
            multiple
            onChange={handleUpload}
            className="hidden"
            data-testid="product-image-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-luxury-outline flex items-center gap-2 text-xs px-4 py-2"
            data-testid="product-upload-btn"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? t("common.loading") : t("admin.upload_photo")}
          </button>
        </div>
        {imageList.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-2">
            {imageList.map((url, i) => (
              <div key={i} className={`relative w-20 h-20 bg-[#F4F0EB] group border ${i === 0 ? "border-[#1A1A1A] border-2" : "border-transparent"}`} data-testid={`image-thumb-${i}`}>
                <img src={url} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute -top-2 left-0 bg-[#1A1A1A] text-white text-[8px] uppercase tracking-widest px-1.5 py-0.5" data-testid={`image-primary-badge-${i}`}>Primary</span>
                )}
                {i !== 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...imageList];
                      const [moved] = next.splice(i, 1);
                      next.unshift(moved);
                      set("images", next.join("\n"));
                    }}
                    className="absolute bottom-0 left-0 right-0 bg-[#1A1A1A]/85 text-white text-[9px] uppercase tracking-widest py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Make primary image"
                    data-testid={`make-primary-${i}`}
                  >
                    Make Primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const updated = imageList.filter((_, idx) => idx !== i).join("\n");
                    set("images", updated);
                  }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[#1A1A1A] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-${i}`}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea value={form.images} onChange={(e) => set("images", e.target.value)} rows={2} placeholder="https://..."
          className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-none focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" data-testid="product-images-input" />
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex gap-6 items-center">
          <label className="flex items-center gap-2 text-xs tracking-wide cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#1A1A1A]" data-testid="product-featured-check" /> {t("admin.featured")}
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.stock_quantity")}</label>
            <Input type="number" min="0" value={form.stock_quantity} onChange={(e) => set("stock_quantity", e.target.value)} className="border-[#E6E2DD] bg-transparent" data-testid="product-stock-quantity-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">{t("admin.discount")} (%)</label>
            <Input type="number" min="0" max="100" value={form.discount} onChange={(e) => set("discount", e.target.value)} className="border-[#E6E2DD] bg-transparent" data-testid="product-discount-input" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Condition (Google Shopping)</label>
          <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide" data-testid="product-condition-select">
            <option value="used">Used (pre-owned)</option>
            <option value="refurbished">Refurbished (restored)</option>
            <option value="new">New</option>
          </select>
        </div>
      </div>
      <div className="flex gap-4">
        <button type="submit" disabled={saving} className="btn-luxury flex-1" data-testid="product-save-btn">{saving ? t("common.loading") : t("admin.save")}</button>
        <button type="button" onClick={onCancel} className="btn-luxury-outline flex-1" data-testid="product-cancel-btn">{t("admin.cancel")}</button>
      </div>
    </form>
  );
}

// ── Sections Editor (Shipping, Returns, Privacy, Terms, Cookies) ──
function SectionsEditor({ pageData, token, onBack }) {
  const [title, setTitle] = useState(pageData?.title || "");
  const [subtitle, setSubtitle] = useState(pageData?.subtitle || "");
  const [sections, setSections] = useState(pageData?.sections || []);
  const [saving, setSaving] = useState(false);

  const updateSection = (i, key, val) => {
    const next = [...sections];
    next[i] = { ...next[i], [key]: val };
    setSections(next);
  };
  const addSection = () => setSections([...sections, { title: "", content: "" }]);
  const removeSection = (i) => setSections(sections.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageData.slug}`, { title, subtitle, sections }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Page saved");
    } catch { toast.error("Error saving page"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="sections-editor">
      <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors mb-8" data-testid="page-editor-back">
        <ChevronLeft size={14} strokeWidth={1.5} /> Back to Pages
      </button>
      <h2 className="text-2xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{PAGE_LABELS[pageData.slug] || pageData.slug}</h2>

      <div className="space-y-4 mb-8">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Page Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="border-[#E6E2DD] bg-transparent" data-testid="page-title-input" />
        </div>
        {(pageData.slug === "privacy" || pageData.slug === "terms" || pageData.slug === "cookies") && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Subtitle</label>
            <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="border-[#E6E2DD] bg-transparent" data-testid="page-subtitle-input" />
          </div>
        )}
      </div>

      <div className="space-y-6">
        {sections.map((s, i) => (
          <div key={i} className="border border-[#E6E2DD] p-4 relative" data-testid={`section-editor-${i}`}>
            <button onClick={() => removeSection(i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`remove-section-${i}`}>
              <X size={14} strokeWidth={1.5} />
            </button>
            <div className="mb-3">
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Section Title</label>
              <Input value={s.title} onChange={(e) => updateSection(i, "title", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Content</label>
              <textarea value={s.content} onChange={(e) => updateSection(i, "content", e.target.value)} rows={4}
                className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-vertical focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-4 mt-6">
        <button onClick={addSection} className="btn-luxury-outline text-[10px] inline-flex items-center gap-2" data-testid="add-section-btn">
          <Plus size={14} strokeWidth={1.5} /> Add Section
        </button>
      </div>
      <div className="mt-8">
        <button onClick={save} disabled={saving} className="btn-luxury" data-testid="save-page-btn">{saving ? "Saving..." : "Save Page"}</button>
      </div>
    </div>
  );
}

// ── FAQ Editor ─────────────────────────────
function FaqEditor({ pageData, token, onBack }) {
  const [title, setTitle] = useState(pageData?.title || "FAQ");
  const [subtitle, setSubtitle] = useState(pageData?.subtitle || "");
  const [items, setItems] = useState(pageData?.faq_items || []);
  const [saving, setSaving] = useState(false);

  const updateItem = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    setItems(next);
  };
  const addItem = () => setItems([...items, { q: "", a: "" }]);
  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/faq`, { title, subtitle, faq_items: items }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("FAQ saved");
    } catch { toast.error("Error saving FAQ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="faq-editor">
      <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors mb-8" data-testid="faq-editor-back">
        <ChevronLeft size={14} strokeWidth={1.5} /> Back to Pages
      </button>
      <h2 className="text-2xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>FAQ</h2>

      <div className="space-y-4 mb-8">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Page Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="border-[#E6E2DD] bg-transparent" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Subtitle</label>
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="border-[#E6E2DD] bg-transparent" />
        </div>
      </div>

      <div className="space-y-4">
        {items.map((item, i) => (
          <div key={i} className="border border-[#E6E2DD] p-4 relative" data-testid={`faq-editor-item-${i}`}>
            <button onClick={() => removeItem(i)} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`remove-faq-${i}`}>
              <X size={14} strokeWidth={1.5} />
            </button>
            <div className="mb-3">
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Question</label>
              <Input value={item.q} onChange={(e) => updateItem(i, "q", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Answer</label>
              <textarea value={item.a} onChange={(e) => updateItem(i, "a", e.target.value)} rows={3}
                className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-vertical focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-4 mt-6">
        <button onClick={addItem} className="btn-luxury-outline text-[10px] inline-flex items-center gap-2" data-testid="add-faq-btn">
          <Plus size={14} strokeWidth={1.5} /> Add Question
        </button>
      </div>
      <div className="mt-8">
        <button onClick={save} disabled={saving} className="btn-luxury" data-testid="save-faq-btn">{saving ? "Saving..." : "Save FAQ"}</button>
      </div>
    </div>
  );
}

// ── Contact Editor ─────────────────────────
function ContactEditor({ pageData, token, onBack }) {
  const [title, setTitle] = useState(pageData?.title || "Contact Us");
  const [contact, setContact] = useState(pageData?.contact || {});
  const [saving, setSaving] = useState(false);

  const setField = (key, val) => setContact((c) => ({ ...c, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/contact`, { title, contact }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contact page saved");
    } catch { toast.error("Error saving"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="contact-editor">
      <button onClick={onBack} className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors mb-8" data-testid="contact-editor-back">
        <ChevronLeft size={14} strokeWidth={1.5} /> Back to Pages
      </button>
      <h2 className="text-2xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Contact Us</h2>

      <div className="space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Page Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="border-[#E6E2DD] bg-transparent" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Intro Text</label>
          <textarea value={contact.intro || ""} onChange={(e) => setField("intro", e.target.value)} rows={2}
            className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-vertical focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Email</label>
            <Input value={contact.email || ""} onChange={(e) => setField("email", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Returns Email</label>
            <Input value={contact.returns_email || ""} onChange={(e) => setField("returns_email", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Phone</label>
            <Input value={contact.phone || ""} onChange={(e) => setField("phone", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">WhatsApp Note</label>
            <Input value={contact.whatsapp || ""} onChange={(e) => setField("whatsapp", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Address (one line per row)</label>
          <textarea value={(contact.address || []).join("\n")} onChange={(e) => setField("address", e.target.value.split("\n"))} rows={4}
            className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-vertical focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Business Hours (one line per row)</label>
          <textarea value={(contact.hours || []).join("\n")} onChange={(e) => setField("hours", e.target.value.split("\n"))} rows={3}
            className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm tracking-wide resize-vertical focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Form Title</label>
          <Input value={contact.form_title || ""} onChange={(e) => setField("form_title", e.target.value)} className="border-[#E6E2DD] bg-transparent" />
        </div>
      </div>
      <div className="mt-8">
        <button onClick={save} disabled={saving} className="btn-luxury" data-testid="save-contact-btn">{saving ? "Saving..." : "Save Contact Page"}</button>
      </div>
    </div>
  );
}

// ── Catalog Manager (Brands & Categories) ──
function CatalogManager({ token }) {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [newCategory, setNewCategory] = useState("");

  const config = { headers: { Authorization: `Bearer ${token}` } };

  const loadBrands = async () => {
    try { const res = await axios.get(`${API}/brands`); setBrands(res.data.map((b) => (typeof b === "string" ? b : b.name))); } catch {}
  };
  const loadCategories = async () => {
    try { const res = await axios.get(`${API}/categories`); setCategories(res.data.map((c) => (typeof c === "string" ? c : c.name))); } catch {}
  };

  useEffect(() => { loadBrands(); loadCategories(); }, []);

  const addBrand = async () => {
    if (!newBrand.trim()) return;
    try { await axios.post(`${API}/admin/brands`, { name: newBrand.trim() }, config); setNewBrand(""); loadBrands(); toast.success("Brand added"); }
    catch { toast.error("Brand already exists or error"); }
  };
  const removeBrand = async (name) => {
    if (!window.confirm(`Delete brand "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/brands/${encodeURIComponent(name)}`, config); loadBrands(); toast.success("Brand deleted"); }
    catch { toast.error("Error"); }
  };
  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try { await axios.post(`${API}/admin/categories`, { name: newCategory.trim() }, config); setNewCategory(""); loadCategories(); toast.success("Category added"); }
    catch { toast.error("Category already exists or error"); }
  };
  const removeCategory = async (name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/categories/${encodeURIComponent(name)}`, config); loadCategories(); toast.success("Category deleted"); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-testid="catalog-manager">
      {/* Brands */}
      <div>
        <h3 className="text-lg mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Brands</h3>
        <div className="flex gap-2 mb-6">
          <Input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="New brand name"
            className="border-[#E6E2DD] bg-transparent flex-1" data-testid="new-brand-input"
            onKeyDown={(e) => e.key === "Enter" && addBrand()} />
          <button onClick={addBrand} className="btn-luxury text-[10px] px-4 py-2" data-testid="add-brand-btn">
            <Plus size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="space-y-0 divide-y divide-[#E6E2DD]">
          {brands.map((name) => (
            <div key={name} className="flex items-center justify-between py-3" data-testid={`brand-item-${name}`}>
              <span className="text-sm tracking-wide">{name}</span>
              <button onClick={() => removeBrand(name)} className="p-1.5 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-brand-${name}`}>
                <Trash2 size={13} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div>
        <h3 className="text-lg mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Categories</h3>
        <div className="flex gap-2 mb-6">
          <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category name"
            className="border-[#E6E2DD] bg-transparent flex-1" data-testid="new-category-input"
            onKeyDown={(e) => e.key === "Enter" && addCategory()} />
          <button onClick={addCategory} className="btn-luxury text-[10px] px-4 py-2" data-testid="add-category-btn">
            <Plus size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="space-y-0 divide-y divide-[#E6E2DD]">
          {categories.map((name) => (
            <div key={name} className="flex items-center justify-between py-3" data-testid={`category-item-${name}`}>
              <span className="text-sm tracking-wide">{name}</span>
              <button onClick={() => removeCategory(name)} className="p-1.5 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-category-${name}`}>
                <Trash2 size={13} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ShippingManager({ token }) {
  const [data, setData] = useState({ zones: [], free_shipping_threshold: 1000, supported_countries: [] });
  const [loading, setLoading] = useState(true);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [thresholdInput, setThresholdInput] = useState("");
  const [editing, setEditing] = useState(null); // zone being edited or "new"

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/shipping/zones`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
      setThresholdInput(String(res.data.free_shipping_threshold ?? ""));
    } catch (e) {
      toast.error("Failed to load shipping");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveThreshold = async () => {
    const val = parseFloat(thresholdInput);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid amount"); return; }
    setSavingThreshold(true);
    try {
      await axios.put(`${API}/admin/shipping/threshold`, { free_shipping_threshold: val }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Threshold updated");
      load();
    } catch { toast.error("Failed to save threshold"); }
    finally { setSavingThreshold(false); }
  };

  const deleteZone = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    try {
      await axios.delete(`${API}/admin/shipping/zones/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Zone deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="text-xs text-[#9CA3AF]">Loading shipping...</div>;

  if (editing) {
    return (
      <ZoneForm
        zone={editing === "new" ? null : editing}
        supportedCountries={data.supported_countries}
        existingZones={data.zones}
        token={token}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="max-w-4xl" data-testid="shipping-manager">
      {/* Free shipping threshold */}
      <div className="bg-[#F4F0EB]/50 p-6 mb-8" data-testid="threshold-block">
        <h3 className="text-sm uppercase tracking-[0.15em] mb-3">Free Shipping Threshold</h3>
        <p className="text-xs text-[#9CA3AF] mb-3">Orders at or above this amount get free shipping for all countries. Set to a very high value (e.g. 99999) to effectively disable.</p>
        <div className="flex gap-3 items-center">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#9CA3AF]">€</span>
            <Input type="number" min="0" step="1" value={thresholdInput} onChange={(e) => setThresholdInput(e.target.value)} className="border-[#E6E2DD] bg-white pl-7" data-testid="threshold-input" />
          </div>
          <button onClick={saveThreshold} disabled={savingThreshold} className="btn-luxury inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-threshold-btn">
            {savingThreshold ? <Loader2 size={14} className="animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>

      {/* Zones list */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm uppercase tracking-[0.15em]">Shipping Zones</h3>
        <button onClick={() => setEditing("new")} className="btn-luxury inline-flex items-center gap-2 text-xs" data-testid="add-zone-btn">
          <Plus size={14} strokeWidth={1.5} /> Add Zone
        </button>
      </div>

      {data.zones.length === 0 ? (
        <p className="text-xs text-[#9CA3AF]">No shipping zones yet. Click &quot;Add Zone&quot; to create one.</p>
      ) : (
        <div className="space-y-3" data-testid="zones-list">
          {data.zones.map((z) => (
            <div key={z.id} className="border border-[#E6E2DD] p-4 flex items-start justify-between gap-4" data-testid={`zone-${z.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm font-medium">{z.name}</span>
                  <span className="text-xs px-2 py-0.5 bg-[#F4F0EB] tracking-wide">
                    {z.price_eur > 0 ? `€${z.price_eur}` : "Free"}
                  </span>
                  {!z.active && <span className="text-[10px] uppercase tracking-wider text-red-600 bg-red-50 px-2 py-0.5">Inactive</span>}
                </div>
                <p className="text-[11px] text-[#9CA3AF] tracking-wide">
                  {z.eta_min_days}–{z.eta_max_days} business days • {z.country_codes.length} countries
                </p>
                <p className="text-[10px] text-[#9CA3AF] mt-1 break-words">
                  {z.country_codes.join(", ")}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => setEditing(z)} className="p-2 border border-[#E6E2DD] hover:border-[#1A1A1A]" data-testid={`edit-zone-${z.id}`}>
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
                <button onClick={() => deleteZone(z.id)} className="p-2 border border-[#E6E2DD] hover:border-red-600 hover:text-red-600" data-testid={`delete-zone-${z.id}`}>
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ZoneForm({ zone, supportedCountries, existingZones, token, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: zone?.name || "",
    country_codes: zone?.country_codes || [],
    price_eur: zone?.price_eur ?? 0,
    eta_min_days: zone?.eta_min_days ?? 2,
    eta_max_days: zone?.eta_max_days ?? 7,
    active: zone?.active !== false,
    sort_order: zone?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  // Countries already used in OTHER zones (uneditable here)
  const usedElsewhere = new Set();
  (existingZones || []).forEach((z) => {
    if (!zone || z.id !== zone.id) {
      (z.country_codes || []).forEach((c) => usedElsewhere.add(c));
    }
  });

  const filtered = (supportedCountries || []).filter((c) =>
    !filter.trim() || c.name.toLowerCase().includes(filter.trim().toLowerCase()) || c.code.toLowerCase().includes(filter.trim().toLowerCase())
  );

  const toggleCountry = (code) => {
    if (usedElsewhere.has(code)) {
      toast.error(`${code} is already in another zone`);
      return;
    }
    setForm((f) => ({
      ...f,
      country_codes: f.country_codes.includes(code)
        ? f.country_codes.filter((c) => c !== code)
        : [...f.country_codes, code],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.country_codes.length === 0) { toast.error("Pick at least one country"); return; }
    if (form.eta_min_days > form.eta_max_days) { toast.error("Min days cannot exceed max days"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        country_codes: form.country_codes,
        price_eur: parseFloat(form.price_eur) || 0,
        eta_min_days: parseInt(form.eta_min_days, 10) || 1,
        eta_max_days: parseInt(form.eta_max_days, 10) || 1,
        active: !!form.active,
        sort_order: parseInt(form.sort_order, 10) || 0,
      };
      if (zone) {
        await axios.put(`${API}/admin/shipping/zones/${zone.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Zone updated");
      } else {
        await axios.post(`${API}/admin/shipping/zones`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Zone created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="zone-form">
      <button onClick={onCancel} className="text-xs text-[#9CA3AF] hover:text-[#1A1A1A] mb-4 inline-flex items-center gap-2" data-testid="zone-back-btn">
        <ChevronLeft size={14} /> Back
      </button>
      <h2 className="text-2xl mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
        {zone ? "Edit Zone" : "Add Zone"}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Name</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Europe Standard" className="border-[#E6E2DD] bg-transparent" data-testid="zone-name" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Price (EUR)</label>
          <Input type="number" min="0" step="0.01" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} className="border-[#E6E2DD] bg-transparent" data-testid="zone-price" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Sort Order</label>
          <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="border-[#E6E2DD] bg-transparent" data-testid="zone-sort" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">ETA Min (days)</label>
          <Input type="number" min="1" value={form.eta_min_days} onChange={(e) => setForm({ ...form, eta_min_days: e.target.value })} className="border-[#E6E2DD] bg-transparent" data-testid="zone-eta-min" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">ETA Max (days)</label>
          <Input type="number" min="1" value={form.eta_max_days} onChange={(e) => setForm({ ...form, eta_max_days: e.target.value })} className="border-[#E6E2DD] bg-transparent" data-testid="zone-eta-max" />
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <input id="zone-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="zone-active" />
          <label htmlFor="zone-active" className="text-xs tracking-wide cursor-pointer">Active (visible to customers)</label>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Countries ({form.country_codes.length} selected)</label>
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search countries..." className="border-[#E6E2DD] bg-transparent text-xs max-w-xs" data-testid="zone-country-filter" />
        </div>
        <div className="border border-[#E6E2DD] max-h-80 overflow-y-auto p-2 grid grid-cols-2 md:grid-cols-3 gap-1" data-testid="zone-countries-grid">
          {filtered.map((c) => {
            const checked = form.country_codes.includes(c.code);
            const disabled = usedElsewhere.has(c.code);
            return (
              <label key={c.code} className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-[#F4F0EB] ${disabled ? "opacity-40 cursor-not-allowed" : ""}`} title={disabled ? "Already in another zone" : ""}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleCountry(c.code)} data-testid={`country-${c.code}`} />
                <span className="text-[10px] text-[#9CA3AF] w-7">{c.code}</span>
                <span className="truncate">{c.name}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button onClick={save} disabled={saving} className="btn-luxury inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-zone-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {zone ? "Update Zone" : "Create Zone"}
        </button>
        <button onClick={onCancel} className="text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A]" data-testid="cancel-zone-btn">
          Cancel
        </button>
      </div>
    </div>
  );
}


// ── Main Admin ─────────────────────────────
export default function Admin() {
  const { t } = useLanguage();
  const [token, setToken] = useState(() => localStorage.getItem("ll_admin_token") || "");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [pages, setPages] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", type: "percentage", value: "", min_order: "", usage_limit: "" });
  const [settings, setSettings] = useState({ admin_email: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const login = (tk) => { setToken(tk); localStorage.setItem("ll_admin_token", tk); };
  const logout = () => { setToken(""); localStorage.removeItem("ll_admin_token"); };

  const loadProducts = async () => { try { const res = await axios.get(`${API}/products?limit=100&_=${Date.now()}`, { headers: { "Cache-Control": "no-cache" } }); setProducts(res.data.products || []); } catch {} };
  const fetchFullProduct = async (p) => {
    try { const res = await axios.get(`${API}/products/${p.id}?_=${Date.now()}`, { headers: { "Cache-Control": "no-cache" } }); setEditing(res.data); }
    catch { setEditing(p); }
  };
  const loadOrders = async () => { try { const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }); setOrders(res.data); } catch {} };
  const loadDiscountCodes = async () => { try { const res = await axios.get(`${API}/admin/discount-codes`, { headers: { Authorization: `Bearer ${token}` } }); setDiscountCodes(res.data); } catch {} };
  const loadSettings = async () => { try { const res = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }); setSettings(res.data); } catch {} };
  const loadPages = async () => {
    try {
      const slugs = ["shipping", "returns", "contact", "faq", "privacy", "terms", "cookies"];
      const results = await Promise.all(slugs.map((s) => axios.get(`${API}/pages/${s}`).then((r) => r.data).catch(() => ({ slug: s }))));
      setPages(results);
    } catch {}
  };

  useEffect(() => {
    if (token) {
      // Validate token on load
      axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => { loadProducts(); loadOrders(); loadPages(); loadDiscountCodes(); loadSettings(); })
        .catch((err) => {
          if (err?.response?.status === 401) { logout(); toast.error("Session expired"); }
          else { loadProducts(); loadOrders(); loadPages(); loadDiscountCodes(); loadSettings(); }
        });
    }
  }, [token]);

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Product deleted"); loadProducts(); }
    catch { toast.error(t("common.error")); }
  };

  if (!token) return <div className="px-6 md:px-12 lg:px-24 py-12" data-testid="admin-page"><AdminLoginForm onLogin={login} t={t} /></div>;

  // Editing a product
  if (editing) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="admin-page">
        <ProductForm product={editing === "new" ? null : editing} token={token} onSave={() => { setEditing(null); loadProducts(); }} onCancel={() => setEditing(null)} t={t} />
      </div>
    );
  }

  // Editing a page
  if (editingPage) {
    const EditorComponent = editingPage.slug === "faq" ? FaqEditor : editingPage.slug === "contact" ? ContactEditor : SectionsEditor;
    return (
      <div className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="admin-page">
        <EditorComponent pageData={editingPage} token={token} onBack={() => { setEditingPage(null); loadPages(); }} />
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="admin-page">
      <div className="flex items-center justify-between mb-12">
        <h1 className="text-3xl md:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>{t("admin.title")}</h1>
        <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors" data-testid="admin-logout-btn">
          <LogOut size={16} strokeWidth={1.5} /> {t("admin.logout")}
        </button>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="bg-[#F4F0EB] mb-8">
          <TabsTrigger value="products" className="text-xs uppercase tracking-widest" data-testid="admin-products-tab">
            <Package size={14} className="mr-2" /> {t("admin.products")}
          </TabsTrigger>
          <TabsTrigger value="orders" className="text-xs uppercase tracking-widest" data-testid="admin-orders-tab">
            <CreditCard size={14} className="mr-2" /> {t("admin.orders")}
          </TabsTrigger>
          <TabsTrigger value="pages" className="text-xs uppercase tracking-widest" data-testid="admin-pages-tab">
            <FileText size={14} className="mr-2" /> Pages
          </TabsTrigger>
          <TabsTrigger value="catalog" className="text-xs uppercase tracking-widest" data-testid="admin-catalog-tab">
            <Tag size={14} className="mr-2" /> Catalog
          </TabsTrigger>
          <TabsTrigger value="discounts" className="text-xs uppercase tracking-widest" data-testid="admin-discounts-tab">
            <Tag size={14} className="mr-2" /> Discounts
          </TabsTrigger>
          <TabsTrigger value="settings" className="text-xs uppercase tracking-widest" data-testid="admin-settings-tab">
            <Settings size={14} className="mr-2" /> Settings
          </TabsTrigger>
          <TabsTrigger value="shipping" className="text-xs uppercase tracking-widest" data-testid="admin-shipping-tab">
            <Truck size={14} className="mr-2" /> Shipping
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <div className="mb-6">
            <button onClick={() => setEditing("new")} className="btn-luxury inline-flex items-center gap-2" data-testid="add-product-btn">
              <Plus size={14} strokeWidth={1.5} /> {t("admin.add")}
            </button>
          </div>
          <div className="space-y-0 divide-y divide-[#E6E2DD]">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 py-4" data-testid={`admin-product-${p.id}`}>
                <div className="w-16 h-20 bg-[#F4F0EB] flex-shrink-0 overflow-hidden">
                  <img src={p.images?.[0] || ""} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#9CA3AF] tracking-wider">{p.brand}</p>
                  <p className="text-sm tracking-wide truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{p.name}</p>
                  <p className="text-xs tracking-wider">&euro;{p.price?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => fetchFullProduct(p)} className="p-2 hover:bg-[#F4F0EB] transition-colors" data-testid={`edit-product-${p.id}`}><Pencil size={14} strokeWidth={1.5} /></button>
                  <button onClick={() => deleteProduct(p.id)} className="p-2 hover:bg-red-50 text-red-500 transition-colors" data-testid={`delete-product-${p.id}`}><Trash2 size={14} strokeWidth={1.5} /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          {orders.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] tracking-wide py-8" data-testid="no-orders">{t("admin.no_orders")}</p>
          ) : (
            <div className="space-y-0 divide-y divide-[#E6E2DD]">
              {orders.map((order) => (
                <div key={order.id} className="py-5" data-testid={`order-${order.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="text-xs text-[#9CA3AF] tracking-wider">#{order.id?.slice(0, 8)}</p>
                        <span className={`text-[10px] uppercase tracking-widest px-3 py-0.5 ${order.payment_status === "paid" ? "bg-green-50 text-green-700" : order.payment_status === "initiated" ? "bg-yellow-50 text-yellow-700" : "bg-red-50 text-red-500"}`} data-testid={`order-status-${order.id}`}>
                          {order.payment_status}
                        </span>
                      </div>
                      <p className="text-sm tracking-wide font-semibold mb-1">&euro;{order.amount?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</p>
                      {order.customer && (order.customer.first_name || order.customer.email) && (
                        <div className="text-xs text-[#1A1A1A]/70 tracking-wide space-y-0.5 mb-2">
                          {(order.customer.first_name || order.customer.last_name) && (
                            <p>{order.customer.first_name} {order.customer.last_name}</p>
                          )}
                          {order.customer.email && <p>{order.customer.email}</p>}
                          {order.customer.phone && <p>{order.customer.phone}</p>}
                          {order.customer.address && (
                            <p>{order.customer.address}{order.customer.city ? `, ${order.customer.city}` : ""}{order.customer.postal_code ? ` ${order.customer.postal_code}` : ""}{order.customer.country ? `, ${order.customer.country}` : ""}</p>
                          )}
                        </div>
                      )}
                      <p className="text-[10px] text-[#9CA3AF] tracking-wider">{order.items?.map((i) => `${i.name} x${i.quantity}`).join(", ")}</p>
                      {order.discount_applied?.code && (
                        <p className="text-[10px] text-green-700 tracking-wider mt-1">Code: {order.discount_applied.code} (-&euro;{order.discount_applied.amount?.toLocaleString("de-DE", { minimumFractionDigits: 2 })})</p>
                      )}
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] tracking-wider">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages">
          <div className="space-y-0 divide-y divide-[#E6E2DD]" data-testid="pages-list">
            {Object.entries(PAGE_LABELS).map(([slug, label]) => {
              const page = pages.find((p) => p.slug === slug) || { slug };
              return (
                <div key={slug} className="flex items-center justify-between py-5" data-testid={`page-item-${slug}`}>
                  <div>
                    <p className="text-sm tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>{label}</p>
                    <p className="text-[10px] text-[#9CA3AF] tracking-wider mt-1">/{slug}</p>
                  </div>
                  <button onClick={() => setEditingPage(page)} className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors" data-testid={`edit-page-${slug}`}>
                    <Pencil size={14} strokeWidth={1.5} /> {t("admin.edit")}
                  </button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Catalog Tab */}
        <TabsContent value="catalog">
          <CatalogManager token={token} />
        </TabsContent>

        {/* Discount Codes Tab */}
        <TabsContent value="discounts">
          <div className="mb-8 p-6 bg-[#F4F0EB]/50" data-testid="create-discount-form">
            <h3 className="text-sm uppercase tracking-[0.15em] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t("admin.create_code")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">Code</label>
                <Input value={newCode.code} onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})} placeholder="SAVE20" className="border-[#E6E2DD] bg-transparent" data-testid="new-code-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">{t("admin.type")}</label>
                <select value={newCode.type} onChange={(e) => setNewCode({...newCode, type: e.target.value})} className="w-full border border-[#E6E2DD] bg-transparent px-3 py-2 text-sm" data-testid="new-code-type">
                  <option value="percentage">% {t("admin.percentage")}</option>
                  <option value="fixed">&euro; {t("admin.fixed_amount")}</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">{t("admin.value")}</label>
                <Input type="number" step="0.01" value={newCode.value} onChange={(e) => setNewCode({...newCode, value: e.target.value})} placeholder={newCode.type === "percentage" ? "20" : "50"} className="border-[#E6E2DD] bg-transparent" data-testid="new-code-value" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">{t("admin.usage_limit")}</label>
                <Input type="number" value={newCode.usage_limit} onChange={(e) => setNewCode({...newCode, usage_limit: e.target.value})} placeholder="0 = unlimited" className="border-[#E6E2DD] bg-transparent" data-testid="new-code-limit" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-1">{t("admin.min_order")} (&euro;)</label>
                <Input type="number" step="0.01" value={newCode.min_order} onChange={(e) => setNewCode({...newCode, min_order: e.target.value})} placeholder="0" className="border-[#E6E2DD] bg-transparent" data-testid="new-code-min-order" />
              </div>
            </div>
            <button
              onClick={async () => {
                if (!newCode.code || !newCode.value) { toast.error("Code and value required"); return; }
                try {
                  await axios.post(`${API}/admin/discount-codes`, {
                    ...newCode,
                    value: parseFloat(newCode.value) || 0,
                    min_order: parseFloat(newCode.min_order) || 0,
                    usage_limit: parseInt(newCode.usage_limit) || 0,
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Discount code created");
                  setNewCode({ code: "", type: "percentage", value: "", min_order: "", usage_limit: "" });
                  loadDiscountCodes();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Error");
                }
              }}
              className="btn-luxury inline-flex items-center gap-2"
              data-testid="create-code-btn"
            >
              <Plus size={14} strokeWidth={1.5} /> {t("admin.create_code")}
            </button>
          </div>

          {discountCodes.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] tracking-wide py-4">No discount codes yet</p>
          ) : (
            <div className="space-y-0 divide-y divide-[#E6E2DD]" data-testid="discount-codes-list">
              {discountCodes.map((dc) => (
                <div key={dc.id} className="flex items-center justify-between py-4" data-testid={`discount-code-${dc.id}`}>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-sm tracking-wider font-semibold" style={{ fontFamily: "'Lato', sans-serif" }}>{dc.code}</p>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${dc.active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-500"}`}>
                        {dc.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="text-xs text-[#9CA3AF] tracking-wide">
                      {dc.type === "percentage" ? `${dc.value}% off` : `€${dc.value?.toLocaleString("de-DE", { minimumFractionDigits: 2 })} off`}
                      {dc.min_order > 0 ? ` | Min: €${dc.min_order}` : ""}
                      {dc.usage_limit > 0 ? ` | Limit: ${dc.usage_count}/${dc.usage_limit}` : ` | Used: ${dc.usage_count || 0}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/admin/discount-codes/${dc.id}`, { active: !dc.active }, { headers: { Authorization: `Bearer ${token}` } });
                          loadDiscountCodes();
                        } catch { toast.error("Error"); }
                      }}
                      className={`text-xs uppercase tracking-widest px-3 py-1 border transition-colors ${dc.active ? "border-red-200 text-red-500 hover:bg-red-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}
                      data-testid={`toggle-code-${dc.id}`}
                    >
                      {dc.active ? "Disable" : "Enable"}
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Delete this code?")) return;
                        try {
                          await axios.delete(`${API}/admin/discount-codes/${dc.id}`, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success("Code deleted");
                          loadDiscountCodes();
                        } catch { toast.error("Error"); }
                      }}
                      className="p-2 hover:bg-red-50 text-red-500 transition-colors"
                      data-testid={`delete-code-${dc.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="max-w-lg space-y-6" data-testid="settings-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2">Notification Email</label>
              <p className="text-xs text-[#9CA3AF] mb-2">Order notifications will be sent to this email address</p>
              <Input
                type="email"
                value={settings.admin_email || ""}
                onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
                placeholder="your@email.com"
                className="border-[#E6E2DD] bg-transparent"
                data-testid="settings-admin-email"
              />
            </div>
            <button
              onClick={async () => {
                if (!settings.admin_email) { toast.error("Email is required"); return; }
                setSavingSettings(true);
                try {
                  await axios.put(`${API}/admin/settings`, { admin_email: settings.admin_email }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Settings saved");
                } catch { toast.error("Error saving settings"); }
                finally { setSavingSettings(false); }
              }}
              disabled={savingSettings}
              className="btn-luxury inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="save-settings-btn"
            >
              {savingSettings ? <Loader2 size={14} className="animate-spin" /> : null}
              Save Settings
            </button>
          </div>
        </TabsContent>

        <TabsContent value="shipping">
          <ShippingManager token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
